import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBack } from '../hooks';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { C, cs } from '../styles/common';
import api from '../api';
import LastChanged, { HistoryLink } from '../components/LastChanged';
import ColourPicker from '../components/forms/ColourPicker';
import { RecipeCard, RecipeCreatorModal } from '../components/RecipeCard';
import { filled } from '../numberFormat';

function resizeImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error(`Cannot load ${file.name} — try a JPG or PNG`)); };
    img.onload = () => {
      const MAX = 1200;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        if (width >= height) { height = Math.round((height / width) * MAX); width = MAX; }
        else                 { width  = Math.round((width / height) * MAX); height = MAX; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      canvas.toBlob(blob => {
        if (!blob) { reject(new Error(`Failed to compress ${file.name}`)); return; }
        resolve(blob);
      }, 'image/jpeg', 0.78);
    };
    img.src = url;
  });
}

const FAMILY_COLORS = {
  PP:   { bg: '#1e3a8a', color: '#93c5fd' },
  PE:   { bg: '#78350f', color: '#fbbf24' },
  HDPE: { bg: '#78350f', color: '#fbbf24' },
  LDPE: { bg: '#78350f', color: '#fbbf24' },
  PS:   { bg: '#374151', color: '#d1d5db' },
  Bio:  { bg: '#14532d', color: '#86efac' },
  PHA:  { bg: '#7f1d1d', color: '#fca5a5' },
  PC:   { bg: '#1f2937', color: '#9ca3af' },
  PA:   { bg: '#3b0764', color: '#d8b4fe' },
  ABS:  { bg: '#431407', color: '#fdba74' },
  POM:  { bg: '#052e16', color: '#4ade80' },
  default: { bg: '#1e2329', color: '#f0f2f5' },
};

export default function Materials() {
  const goBack   = useBack('/admin');
  const { isDirector } = useAuth();
  const [materials, setMaterials]       = useState([]);
  const [colours, setColours]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [tab, setTab]                   = useState(() => (new URLSearchParams(window.location.search).get('tab') === 'recipes' ? 'recipes' : 'material'));
  const [selected, setSelected]         = useState(null);
  const [editing, setEditing]           = useState(null);
  const [recipesCount, setRecipesCount] = useState(null);
  const [recipeAddTrigger, setRecipeAddTrigger] = useState(0);
  const [search, setSearch]             = useState('');
  const [filterFamily, setFilterFamily] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterMfi, setFilterMfi]       = useState('');
  const [filterColour, setFilterColour] = useState('');

  function switchTab(key) {
    setTab(key);
    setSearch(''); setFilterFamily(''); setFilterSupplier(''); setFilterMfi(''); setFilterColour('');
  }

  function load() {
    Promise.all([
      api.get('/api/v1/materials'),
      api.get('/api/v1/colours').catch(() => ({ data: [] })),
      api.get('/api/v1/recipes').catch(() => ({ data: [] })),
    ]).then(([m, cl, r]) => {
      setMaterials(m.data);
      setColours(cl.data);
      setRecipesCount(r.data.length);
      setLoading(false);
    }).catch(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  if (loading) return <div style={s.center}>Loading materials...</div>;

  if (editing !== null) return (
    <MaterialForm
      material={editing === 'new' ? null : editing}
      defaultType={tab}
      isDirector={isDirector()}
      onSaved={() => { setEditing(null); setSelected(null); load(); }}
      onCancel={() => setEditing(null)}
    />
  );

  if (selected) return (
    <MaterialDetail
      material={selected}
      isDirector={isDirector()}
      onEdit={() => setEditing(selected)}
      onBack={() => setSelected(null)}
      onDeleted={() => { setSelected(null); load(); }}
      onUpdated={updated => { setSelected(updated); load(); }}
    />
  );

  const tabMaterials = materials.filter(m => (m.type || 'material') === tab);
  const families  = [...new Set(tabMaterials.map(m => m.family).filter(Boolean))].sort();
  const suppliers = [...new Set(tabMaterials.map(m => m.manufacturer).filter(Boolean))].sort();
  const mbColours = [...new Set(tabMaterials.map(m => m.colour).filter(Boolean))].sort();
  const filtered  = tabMaterials
    .filter(m => !search || [m.name, m.manufacturer].some(v => v?.toLowerCase().includes(search.toLowerCase())))
    .filter(m => !filterFamily   || m.family === filterFamily)
    .filter(m => !filterSupplier || m.manufacturer === filterSupplier)
    .filter(m => !filterColour   || m.colour === filterColour)
    .filter(m => {
      if (!filterMfi) return true;
      const v = parseFloat(m.mfi);
      if (isNaN(v)) return false;
      if (filterMfi === '<5')   return v < 5;
      if (filterMfi === '5-15') return v >= 5  && v < 15;
      if (filterMfi === '15-50')return v >= 15 && v < 50;
      if (filterMfi === '>50')  return v >= 50;
      return true;
    });

  const isMobile = window.innerWidth < 600;
  const TAB_FULL = { material: 'Materials', masterbatch: 'Masterbatches', additive: 'Additives', recipes: 'Recipes' };
  const tabLabels = {
    material:    'Materials',
    masterbatch: isMobile ? 'MB' : 'Masterbatches',
    additive:    isMobile ? 'Add' : 'Additives',
    recipes:     'Recipes',
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <button onClick={goBack} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '12px', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
          <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px' }} /> Back
        </button>
        <span style={{ color: '#2a3040' }}>·</span>
        <span style={{ fontSize: '12px', color: '#f0f2f5' }}>Materials</span>
      </div>
      <h1 style={s.title}>Materials</h1>
          <p style={s.sub}>{tab !== 'recipes' ? `${filtered.length} ${TAB_FULL[tab].toLowerCase()} in catalogue` : 'All recipes'}</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <HistoryLink type={tab === 'recipes' ? 'recipe' : 'material'} />
          {isDirector() && (
            tab === 'recipes'
              ? <button style={s.addBtn} onClick={() => setRecipeAddTrigger(t => t + 1)}>+ Add Recipe</button>
              : <button style={s.addBtn} onClick={() => setEditing('new')}>+ Add {TAB_FULL[tab].slice(0,-1)}</button>
          )}
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={s.subTabs}>
        {Object.entries(tabLabels).map(([key, label]) => (
          <button key={key}
            style={{ ...s.subTab, ...(tab === key ? s.subTabActive : {}) }}
            onClick={() => switchTab(key)}>
            {label}
            {key !== 'recipes'
              ? <span style={s.tabCount}>{materials.filter(m => (m.type || 'material') === key).length}</span>
              : recipesCount !== null && <span style={s.tabCount}>{recipesCount}</span>}
          </button>
        ))}
      </div>

      {tab === 'recipes' ? (
        <RecipesTab isDirector={isDirector()} colours={colours} onCountChange={setRecipesCount} addTrigger={recipeAddTrigger} />
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 180px' }}>
            <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#4b5563', fontSize: '12px', pointerEvents: 'none' }} />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search name or manufacturer…"
              style={{ ...s.formInput, paddingLeft: '30px', width: '100%', boxSizing: 'border-box' }} />
          </div>
          <select value={filterFamily} onChange={e => setFilterFamily(e.target.value)} style={{ ...s.formInput, flex: '0 1 140px' }}>
            <option value="">All families</option>
            {families.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <select value={filterSupplier} onChange={e => setFilterSupplier(e.target.value)} style={{ ...s.formInput, flex: '0 1 160px' }}>
            <option value="">All manufacturers</option>
            {suppliers.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {tab === 'material' && (
            <select value={filterMfi} onChange={e => setFilterMfi(e.target.value)} style={{ ...s.formInput, flex: '0 1 130px' }}>
              <option value="">All MFI</option>
              <option value="<5">&lt; 5 g/10min</option>
              <option value="5-15">5 – 15 g/10min</option>
              <option value="15-50">15 – 50 g/10min</option>
              <option value=">50">&gt; 50 g/10min</option>
            </select>
          )}
          {tab === 'masterbatch' && mbColours.length > 0 && (
            <select value={filterColour} onChange={e => setFilterColour(e.target.value)} style={{ ...s.formInput, flex: '0 1 140px' }}>
              <option value="">All colours</option>
              {mbColours.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          {(search || filterFamily || filterSupplier || filterMfi || filterColour) && (
            <button onClick={() => { setSearch(''); setFilterFamily(''); setFilterSupplier(''); setFilterMfi(''); setFilterColour(''); }}
              style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              Clear filters
            </button>
          )}
        </div>
      )}

      {tab !== 'recipes' && <div style={s.grid}>
        {filtered.map(mat => {
          const fc = FAMILY_COLORS[mat.family] || FAMILY_COLORS.default;
          const stockOk = mat.stock_kg >= mat.stock_min_kg;
          const colourHex = mat.colour ? colours.find(c => c.name === mat.colour)?.hex : null;
          return (
            <div key={mat.id} style={s.card} onClick={() => setSelected(mat)}>
              <div style={{ height: '3px', background: fc.color }} />
              <div style={s.cardBody}>
                <div style={s.cardHeader}>
                  <span style={{ ...s.family, background: fc.bg, color: fc.color }}>{mat.family}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {tab === 'masterbatch' && (
                      <span
                        style={{
                          width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
                          background: colourHex || '#374151',
                          border: colourHex ? '2px solid rgba(255,255,255,0.2)' : '2px dashed #4b5563',
                        }}
                        title={mat.colour || 'No colour set'}
                      />
                    )}
                    <span style={{ ...s.stockDot, background: stockOk ? '#22c55e' : '#ef4444' }} />
                  </div>
                </div>
                <div style={s.matName}>{mat.name}</div>
                <div style={s.manufacturer}>{mat.manufacturer}</div>
                {tab === 'material' && (
                  <div style={s.specs}>
                    <Spec label="Melt window" value={`${mat.tmelt_min}–${mat.tmelt_max}°C`} highlight />
                    <Spec label="Mould temp"  value={`${mat.tmould_rec}°C`} />
                    <Spec label="Stock"       value={`${mat.stock_kg}kg`} />
                    <Spec label="Drying"      value={mat.dry_required ? `${mat.dry_temp}°C / ${mat.dry_time}h` : 'Not required'} />
                  </div>
                )}
                {tab === 'masterbatch' && (
                  <div style={s.specs}>
                    <Spec label="Stock" value={`${mat.stock_kg}kg`} />
                    <Spec label="Status" value={mat.status} />
                  </div>
                )}
                {tab === 'additive' && (
                  <div style={s.specs}>
                    <Spec label="Stock"  value={`${mat.stock_kg}kg`} />
                    <Spec label="Status" value={mat.status} />
                  </div>
                )}
              </div>
              <div style={s.cardFooter}>
                <span style={{
                  ...s.statusChip,
                  background: stockOk ? '#166534' : '#7f1d1d',
                  color: stockOk ? '#86efac' : '#fca5a5',
                }}>
                  {stockOk ? `In stock · ${mat.stock_kg}kg` : 'Low stock'}
                </span>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div style={s.empty}>
            <div style={s.emptyText}>No {TAB_FULL[tab].toLowerCase()} yet</div>
            {isDirector() && <div style={s.emptySub}>Click "+ Add" to add one</div>}
          </div>
        )}
      </div>}
    </div>
  );
}

// ── RECIPES TAB ───────────────────────────────────────────────
function RecipesTab({ isDirector, colours, onCountChange, addTrigger }) {
  const navigate = useNavigate();
  const [recipes, setRecipes]             = useState([]);
  const [parts, setParts]                 = useState([]);
  const [loading, setLoading]             = useState(true);
  const [search, setSearch]               = useState('');
  const [filters, setFilters]             = useState({ baseMaterial: '', baseFamily: '', masterbatch: '', additive: '', colour: '', part: '' });
  const [viewMode, setViewMode]           = useState('pct');
  const [showCreator, setShowCreator]     = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [partsModal, setPartsModal]       = useState(null);

  function load() {
    Promise.all([
      api.get('/api/v1/recipes'),
      api.get('/api/v1/parts').catch(() => ({ data: [] })),
    ]).then(([r, p]) => {
      setRecipes(r.data);
      setParts(p.data);
      setLoading(false);
      onCountChange?.(r.data.length);
    }).catch(() => setLoading(false));
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (addTrigger > 0) { setEditingRecipe(null); setShowCreator(true); } }, [addTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  function deleteRecipe(id) {
    const recipe  = recipes.find(r => r.id === id);
    const usedBy  = parts.filter(p => (p.part_recipes || []).some(pr => pr.recipe?.id === id));
    if (usedBy.length > 0) {
      setConfirmDelete({ id, recipeName: recipe?.name, usedBy });
    } else {
      api.delete(`/api/v1/recipes/${id}`).then(() => load());
    }
  }

  function confirmAndDelete() {
    api.delete(`/api/v1/recipes/${confirmDelete.id}`).then(() => { setConfirmDelete(null); load(); });
  }

  const uniq = arr => [...new Set(arr.filter(Boolean))].sort();
  const baseMatOptions = uniq(recipes.map(r => r.material?.name));
  const familyOptions  = uniq(recipes.map(r => r.material?.family));
  const mbOptions      = uniq(recipes.flatMap(r => (r.recipe_components || []).filter(c => c.type === 'masterbatch').map(c => c.material?.name)));
  const addOptions     = uniq(recipes.flatMap(r => (r.recipe_components || []).filter(c => c.type === 'additive').map(c => c.material?.name)));
  const colourOptions  = uniq(recipes.flatMap(r => (r.recipe_components || []).filter(c => c.type === 'masterbatch').map(c => c.material?.colour)));
  const partOptions    = uniq(parts.map(p => p.name));

  const setFilter = (key, val) => setFilters(f => ({ ...f, [key]: val }));
  const hasFilters = search || Object.values(filters).some(Boolean);

  const filtered = recipes.filter(r => {
    const q = search.toLowerCase();
    if (q && !r.name.toLowerCase().includes(q) && !(r.description || '').toLowerCase().includes(q) && !(r.material?.name || '').toLowerCase().includes(q)) return false;
    if (filters.baseMaterial && r.material?.name !== filters.baseMaterial) return false;
    if (filters.baseFamily   && r.material?.family !== filters.baseFamily) return false;
    const comps = r.recipe_components || [];
    if (filters.masterbatch && !comps.some(c => c.type === 'masterbatch' && c.material?.name === filters.masterbatch)) return false;
    if (filters.additive    && !comps.some(c => c.type === 'additive'    && c.material?.name === filters.additive))    return false;
    if (filters.colour      && !comps.some(c => c.type === 'masterbatch' && c.material?.colour === filters.colour))    return false;
    if (filters.part        && !parts.some(p => p.name === filters.part && (p.part_recipes || []).some(pr => pr.recipe?.id === r.id))) return false;
    return true;
  });

  return (
    <div>
      {(showCreator || editingRecipe) && (
        <RecipeCreatorModal
          mouldId={null}
          recipe={editingRecipe}
          onSaved={() => { setShowCreator(false); setEditingRecipe(null); load(); }}
          onClose={() => { setShowCreator(false); setEditingRecipe(null); }}
        />
      )}

      {partsModal && (
        <div style={s.overlay} onClick={() => setPartsModal(null)}>
          <div style={{ ...s.modal, maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <div style={s.modalTitle}>
                <i className="fa-solid fa-cube" style={{ color: '#14b8a6', marginRight: '8px', fontSize: '14px' }} />
                {partsModal.name}
              </div>
              <button style={s.closeBtn} onClick={() => setPartsModal(null)}>✕</button>
            </div>
            <div style={s.modalBody}>
              {(() => {
                const linked = parts.filter(p => (p.part_recipes || []).some(pr => pr.recipe?.id === partsModal.id));
                if (linked.length === 0) return (
                  <div style={{ textAlign: 'center', padding: '24px 0', color: '#6b7280', fontSize: '13px' }}>
                    <i className="fa-solid fa-cube" style={{ fontSize: '28px', color: '#2a3040', display: 'block', marginBottom: '10px' }} />
                    This recipe is not linked to any parts yet
                  </div>
                );
                return (
                  <>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                      Used in {linked.length} part{linked.length !== 1 ? 's' : ''}
                    </div>
                    {linked.map(p => (
                      <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #2a3040' }}>
                        <i className="fa-solid fa-cube" style={{ color: '#14b8a6', fontSize: '12px', flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: '600' }}>{p.name}</div>
                          {p.part_number && <div style={{ fontSize: '10px', color: '#14b8a6', fontFamily: 'monospace' }}>{p.part_number}</div>}
                          {p.customer && <div style={{ fontSize: '11px', color: '#6b7280' }}>{p.customer}</div>}
                        </div>
                        {(() => {
                          const pr = p.part_recipes.find(pr => pr.recipe?.id === partsModal.id);
                          return pr?.is_primary
                            ? <span style={{ fontSize: '9px', background: '#14b8a6', color: '#fff', padding: '2px 6px', borderRadius: '4px', fontWeight: '700' }}>Primary</span>
                            : null;
                        })()}
                      </div>
                    ))}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div style={s.overlay} onClick={() => setConfirmDelete(null)}>
          <div style={{ ...s.modal, maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <div style={s.modalTitle}>Delete recipe?</div>
              <button style={s.closeBtn} onClick={() => setConfirmDelete(null)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <div style={{ fontSize: '13px', color: '#f0f2f5', marginBottom: '12px' }}>
                <strong>{confirmDelete.recipeName}</strong> is linked to {confirmDelete.usedBy.length} part{confirmDelete.usedBy.length !== 1 ? 's' : ''}:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
                {confirmDelete.usedBy.map(p => (
                  <div key={p.id} style={{ fontSize: '12px', background: '#0f1117', border: '1px solid #2a3040', borderRadius: '6px', padding: '6px 10px', color: '#9ca3af' }}>
                    {p.name}
                  </div>
                ))}
              </div>
              <div style={{ background: '#3b0f0f', border: '1px solid #7f1d1d', borderRadius: '8px', padding: '10px 12px', fontSize: '12px', color: '#fca5a5' }}>
                Deleting this recipe will remove it from {confirmDelete.usedBy.length === 1 ? 'this part' : 'these parts'}. This cannot be undone.
              </div>
            </div>
            <div style={s.modalFooter}>
              <button style={s.backBtn} onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button style={{ ...s.deleteBtn, padding: '10px 20px' }} onClick={confirmAndDelete}>Delete recipe</button>
            </div>
          </div>
        </div>
      )}

      {/* Toolbar + Filters — single unified row */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ flex: '2 1 180px', position: 'relative' }}>
          <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: '12px', pointerEvents: 'none' }} />
          <input
            style={{ ...s.formInput, width: '100%', paddingLeft: '30px', boxSizing: 'border-box' }}
            placeholder="Search recipes, materials..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {[
          ['baseMaterial', 'Base mat.',   baseMatOptions],
          ['baseFamily',   'Family',      familyOptions],
          ['masterbatch',  'Masterbatch', mbOptions],
          ['additive',     'Additive',    addOptions],
          ['colour',       'Colour',      colourOptions],
          ['part',         'Part',        partOptions],
        ].map(([key, label, options]) => (
          <select key={key}
            style={{ ...s.formInput, flex: '1 1 110px', maxWidth: '160px', fontSize: '12px', padding: '6px 8px', color: filters[key] ? '#f0f2f5' : '#6b7280' }}
            value={filters[key]}
            onChange={e => setFilter(key, e.target.value)}
          >
            <option value="">{label}</option>
            {options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ))}
        <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
          {[['pct', '%'], ['kg', '1kg'], ['bag', '25kg']].map(([v, l]) => (
            <button key={v} style={{ ...s.viewToggleBtn, ...(viewMode === v ? s.viewToggleBtnActive : {}) }} onClick={() => setViewMode(v)}>{l}</button>
          ))}
        </div>
        {hasFilters && (
          <button
            style={{ ...s.backBtn, fontSize: '11px', padding: '6px 10px', flexShrink: 0 }}
            onClick={() => { setSearch(''); setFilters({ baseMaterial: '', baseFamily: '', masterbatch: '', additive: '', colour: '', part: '' }); }}
          >
            <i className="fa-solid fa-xmark" style={{ marginRight: '4px' }} />Clear
          </button>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ fontSize: '12px', color: '#6b7280' }}>
          {filtered.length} recipe{filtered.length !== 1 ? 's' : ''}{hasFilters ? ` of ${recipes.length} total` : ''}
        </div>
        <button
          style={{ ...s.backBtn, fontSize: '11px', padding: '5px 10px', display: 'flex', alignItems: 'center', gap: '5px' }}
          onClick={() => navigate('/admin/archive/recipes')}
        >
          <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: '10px' }} />Change History
        </button>
      </div>

      {loading ? (
        <div style={s.center}>Loading recipes...</div>
      ) : filtered.length === 0 ? (
        <div style={{ background: '#1e2329', border: '1px solid #2a3040', borderRadius: '10px', padding: '40px', textAlign: 'center' }}>
          <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '12px' }}>
            {hasFilters ? 'No recipes match the current filters' : 'No recipes yet'}
          </div>
          {isDirector && !hasFilters && (
            <button style={s.addBtn} onClick={() => setShowCreator(true)}>+ Create first recipe</button>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
          {filtered.map(recipe => (
            <RecipeCard
              key={recipe.id}
              recipe={recipe}
              colours={colours}
              viewMode={viewMode}
              isDirector={isDirector}
              onEdit={r => { setEditingRecipe(r); setShowCreator(false); }}
              onDelete={deleteRecipe}
              onShowParts={r => setPartsModal(r)}
              onHistory={r => navigate(`/admin/archive/recipes?recipe_id=${r.id}`)}
              showMeta
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── DATASHEET BUTTON (detail view) ────────────────────────────
function DatasheetButton({ url }) {
  if (!url) return null;
  const isPdf = url.toLowerCase().includes('.pdf') || url.toLowerCase().includes('supabase');
  return (
    <a href={url} target="_blank" rel="noreferrer"
      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#1e2329',
        border: '1px solid #2a3040', borderRadius: '8px', padding: '8px 14px',
        fontSize: '12px', color: '#9ca3af', textDecoration: 'none', cursor: 'pointer' }}>
      <i className={`fa-solid ${isPdf ? 'fa-file-pdf' : 'fa-arrow-up-right-from-square'}`}
        style={{ color: isPdf ? '#ef4444' : '#14b8a6' }} />
      Datasheet
    </a>
  );
}

// ── MATERIAL DETAIL ───────────────────────────────────────────
function MaterialDetail({ material, isDirector, onEdit, onBack, onDeleted }) {
  const { confirm } = useToast();
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const fc = FAMILY_COLORS[material.family] || FAMILY_COLORS.default;

  function deleteMaterial() {
    confirm({
      message: `Delete ${material.name}? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: () => doDelete(),
    });
  }

  function doDelete() {
    setDeleting(true);
    setDeleteError('');
    api.delete(`/api/v1/materials/${material.id}`)
      .then(() => onDeleted())
      .catch(err => {
        setDeleting(false);
        const msg = err.response?.data?.error || err.message || '';
        if (msg.toLowerCase().includes('foreign key') || msg.toLowerCase().includes('violates') || msg.toLowerCase().includes('referenced')) {
          setDeleteError('This material is used in one or more recipes. Remove it from those recipes first, then delete.');
        } else {
          setDeleteError(msg || 'Could not delete material.');
        }
      });
  }

  const isMaterial = (material.type || 'material') === 'material';

  return (
    <div style={s.page}>
      <div style={{ height: '4px', background: fc.color, borderRadius: '4px', marginBottom: '20px' }} />

      <div style={s.detailHeader}>
        <button style={s.backBtn} onClick={onBack}>← Back</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <DatasheetButton url={material.datasheet_url} />
          {isDirector && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
              <div style={s.detailActions}>
                <button style={s.editBtn} onClick={onEdit}>Edit</button>
                <button style={s.deleteBtn} onClick={deleteMaterial} disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
              {deleteError && (
                <div style={{ fontSize: '11px', color: '#fca5a5', maxWidth: '260px', textAlign: 'right', lineHeight: '1.4' }}>
                  {deleteError}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div style={s.titleRow}>
        <span style={{ ...s.family, background: fc.bg, color: fc.color, fontSize: '12px', padding: '4px 10px' }}>
          {material.family}
        </span>
        <span style={s.typeChip}>{material.type || 'material'}</span>
      </div>
      <div style={s.detailTitle}>{material.name}</div>
      <div style={s.detailSub}>{material.manufacturer} · {material.application || ''}</div>

      <div style={s.detailGrid}>
        {isMaterial && (
          <Section title="Processing temperatures">
            <Row label="Melt temp min"   value={`${material.tmelt_min}°C`} />
            <Row label="Melt temp max"   value={`${material.tmelt_max}°C`} />
            <Row label="Mould temp rec"  value={`${material.tmould_rec}°C`} />
            <Row label="Ejection temp"   value={material.t_ejection ? `${material.t_ejection}°C` : '—'} />
            <Row label="Glass transition (Tg)" value={material.tg ? `${material.tg}°C` : '—'} />
          </Section>
        )}

        {isMaterial && (
          <Section title="Physics constants">
            <Row label="Thermal diffusivity (α)" value={material.alpha ? `${material.alpha.toExponential(2)} m²/s` : '—'} />
            <Row label="Specific heat (Cp)"      value={material.cp ? `${material.cp} J/kg·K` : '—'} />
            <Row label="Melt density"            value={material.density ? `${material.density} kg/m³` : '—'} />
            <Row label="Solid density"           value={material.dens_solid ? `${material.dens_solid} kg/m³` : '—'} />
            <Row label="Max shear rate"          value={material.shear_max ? `${material.shear_max.toLocaleString()} s⁻¹` : '—'} />
            <Row label="MFI"                     value={material.mfi ? `${material.mfi} g/10min (${material.mfi_cond || ''})` : '—'} />
            <Row label="Shrinkage L"             value={material.shrink_l ? `${material.shrink_l}%` : '—'} />
            <Row label="Shrinkage T"             value={material.shrink_t ? `${material.shrink_t}%` : '—'} />
          </Section>
        )}

        {isMaterial && (
          <Section title="Controller settings">
            <Row label="Back pressure rec"  value={material.back_pres_rec || '—'} />
            <Row label="Screw speed rec"    value={material.screw_spd_rec || '—'} />
            <Row label="Cushion rec"        value={material.cushion_rec   || '—'} />
            <Row label="Max regrind"        value={material.regrind_max ? `${material.regrind_max}%` : '—'} />
          </Section>
        )}

        {isMaterial && material.dry_required && (
          <Section title="Drying">
            <Row label="Required"        value="Yes" />
            <Row label="Drying temp"     value={`${material.dry_temp}°C`} />
            <Row label="Drying time min" value={`${material.dry_time}h`} />
            <Row label="Drying time max" value={material.dry_max ? `${material.dry_max}h` : '—'} />
            <Row label="Max moisture"    value={material.moisture_max_ppm ? `${material.moisture_max_ppm}ppm` : '—'} />
            <Row label="Dewpoint target" value={material.dewpoint_target ? `${material.dewpoint_target}°C` : '—'} />
            <Row label="Hygroscopic"     value={material.hygroscopic ? 'Yes' : 'No'} />
          </Section>
        )}

        <Section title="Stock">
          <Row label="Current stock"  value={`${material.stock_kg}kg`} />
          <Row label="Minimum stock"  value={`${material.stock_min_kg}kg`} />
          <Row label="Reorder qty"    value={material.reorder_qty_kg ? `${material.reorder_qty_kg}kg` : '—'} />
          {isDirector && <Row label="Supplier"       value={material.supplier || '—'} />}
          <Row label="Status"         value={material.status} />
        </Section>

        {/* Cost — director only */}
        {isDirector && (
          <Section title="Commercial">
            <Row label="Cost per kg" value={material.cost_per_kg ? `€${material.cost_per_kg}/kg` : '—'} />
            <Row label="Stock value" value={material.cost_per_kg && material.stock_kg
              ? `€ ${(material.cost_per_kg * material.stock_kg).toFixed(2).replace('.', ',')}`
              : '—'} />
          </Section>
        )}

        <Section title="Compliance">
          <Row label="Food contact"   value={material.food_contact   ? 'Yes' : 'No'} />
          <Row label="Medical grade"  value={material.medical_grade  ? 'Yes' : 'No'} />
          <Row label="RoHS"           value={material.rohs           ? 'Yes' : 'No'} />
          <Row label="REACH"          value={material.reach          ? 'Yes' : 'No'} />
        </Section>
      </div>

      {material.notes && (
        <div style={{ ...s.notesBox, marginTop: '16px' }}>
          <div style={s.notesTitle}>ℹ Notes</div>
          <div style={s.notesText}>{material.notes}</div>
        </div>
      )}
    </div>
  );
}

// ── DOCUMENT PANEL ────────────────────────────────────────────
function DocumentPanel({ onExtracted, datasheetUrl, onUrlChange }) {
  const [mode, setMode]             = useState(null);
  const [scanFiles, setScanFiles]   = useState([]);
  const [scanPreviews, setScanPreviews] = useState([]);
  const [scanning, setScanning]     = useState(false);
  const [scanError, setScanError]   = useState('');
  const [pdfExtracting, setPdfExtracting] = useState(false);
  const [pdfError, setPdfError]     = useState('');
  const [pdfExtracted, setPdfExtracted] = useState(false);
  const [urlExtracting, setUrlExtracting] = useState(false);
  const [urlError, setUrlError]     = useState('');
  const scanRef = useRef();
  const pdfRef  = useRef();

  const OPTIONS = [
    { key: 'scan', icon: 'fa-camera',   color: '#1d4ed8', label: 'Scan images', desc: 'AI reads TDS photos and fills the form fields automatically' },
    { key: 'pdf',  icon: 'fa-file-pdf', color: '#dc2626', label: 'Upload PDF',  desc: 'AI reads the PDF and fills the form fields automatically' },
    { key: 'url',  icon: 'fa-link',     color: '#14b8a6', label: 'Paste URL',   desc: 'AI reads the page and fills the form; link saved as reference' },
  ];

  function addScanFiles(incoming) {
    const valid = Array.from(incoming).filter(f => f.type.startsWith('image/'));
    if (!valid.length) return;
    setScanPreviews(p => [...p, ...valid.map(f => URL.createObjectURL(f))]);
    setScanFiles(f => [...f, ...valid]);
    setScanError('');
  }
  function removeScanFile(i) {
    URL.revokeObjectURL(scanPreviews[i]);
    setScanFiles(f => f.filter((_, idx) => idx !== i));
    setScanPreviews(p => p.filter((_, idx) => idx !== i));
  }
  async function runScan() {
    if (!scanFiles.length) return;
    setScanning(true); setScanError('');
    try {
      const blobs = await Promise.all(scanFiles.map(resizeImage));
      const fd = new FormData();
      blobs.forEach((blob, i) => fd.append('images', blob, scanFiles[i].name + '.jpg'));
      const res = await api.post('/api/v1/materials/extract', fd);
      onExtracted(res.data);
    } catch (e) {
      setScanError(e.response?.data?.error || (!e.response ? 'Cannot reach server' : `Error ${e.response.status}: ${e.response.statusText}`));
    }
    setScanning(false);
  }

  async function handlePdfExtract(e) {
    const file = e.target.files[0];
    if (!file) return;
    setPdfExtracting(true); setPdfError(''); setPdfExtracted(false);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/api/v1/materials/extract-pdf', fd);
      onExtracted(data);
      setPdfExtracted(true);
    } catch (err) {
      setPdfError(err.response?.data?.error || 'Extraction failed — try again');
    } finally {
      setPdfExtracting(false);
      e.target.value = '';
    }
  }

  async function handleUrlExtract() {
    if (!datasheetUrl) return;
    setUrlExtracting(true); setUrlError('');
    try {
      const { data } = await api.post('/api/v1/materials/extract-url', { url: datasheetUrl });
      onExtracted(data);
    } catch (err) {
      setUrlError(err.response?.data?.error || 'Extraction failed — try again');
    } finally {
      setUrlExtracting(false);
    }
  }

  return (
    <div style={{ background: '#1e2329', border: '1px solid #2a3040', borderRadius: '14px', padding: '18px', marginBottom: '20px' }}>
      <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b7280', marginBottom: '12px' }}>
        Quick-start — pick one to populate or attach a datasheet
      </div>

      {/* 3 option cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: mode ? '18px' : 0 }}>
        {OPTIONS.map(opt => {
          const isActive   = mode === opt.key;
          const hasContent = (opt.key === 'pdf' && pdfExtracted) || (opt.key === 'url' && !!datasheetUrl);
          return (
            <button key={opt.key} type="button" onClick={() => setMode(m => m === opt.key ? null : opt.key)}
              style={{
                background: isActive ? opt.color + '18' : '#0f1117',
                border: `1.5px solid ${isActive ? opt.color : '#2a3040'}`,
                borderRadius: '10px', padding: '12px 14px', cursor: 'pointer', textAlign: 'left',
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                <i className={`fa-solid ${opt.icon}`} style={{ color: isActive ? opt.color : '#4b5563', fontSize: '15px' }} />
                <span style={{ fontSize: '12px', fontWeight: '700', color: isActive ? '#f0f2f5' : '#9ca3af' }}>{opt.label}</span>
                {hasContent && <span style={{ marginLeft: 'auto', width: '7px', height: '7px', borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} title="Attached" />}
              </div>
              <div style={{ fontSize: '10px', color: '#6b7280', lineHeight: '1.45' }}>{opt.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Scan images */}
      {mode === 'scan' && (
        <div style={{ borderTop: '1px solid #2a3040', paddingTop: '16px' }}>
          <div style={s.dropZone}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); addScanFiles(e.dataTransfer.files); }}
            onClick={() => scanRef.current.click()}>
            <input ref={scanRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
              onChange={e => addScanFiles(e.target.files)} />
            <div style={{ fontSize: '20px', marginBottom: '5px' }}>⬆</div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              {scanFiles.length === 0 ? 'Drop TDS photos here or click to select'
                : `${scanFiles.length} image${scanFiles.length !== 1 ? 's' : ''} selected — add more or scan`}
            </div>
          </div>
          {scanPreviews.length > 0 && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
              {scanPreviews.map((url, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  <img src={url} alt="" style={s.previewImg} />
                  <button type="button" style={s.removePreview} onClick={() => removeScanFile(i)}>×</button>
                </div>
              ))}
            </div>
          )}
          {scanError && (
            <div style={{ fontSize: '12px', color: '#fca5a5', marginTop: '8px', padding: '8px 10px', background: '#7f1d1d', borderRadius: '6px' }}>{scanError}</div>
          )}
          {scanFiles.length > 0 && (
            <button type="button" style={{ ...s.scanBtn, opacity: scanning ? 0.7 : 1, marginTop: '10px' }} disabled={scanning} onClick={runScan}>
              {scanning ? '🔍 Analysing…' : '🔍 Scan & fill form'}
            </button>
          )}
        </div>
      )}

      {/* Upload PDF */}
      {mode === 'pdf' && (
        <div style={{ borderTop: '1px solid #2a3040', paddingTop: '16px' }}>
          <input ref={pdfRef} type="file" accept=".pdf,application/pdf" style={{ display: 'none' }} onChange={handlePdfExtract} />
          {pdfExtracted ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#052e16', border: '1px solid #166534', borderRadius: '8px', padding: '12px 14px' }}>
              <i className="fa-solid fa-circle-check" style={{ color: '#22c55e', fontSize: '20px', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '12px', fontWeight: '600', color: '#f0f2f5' }}>Form filled from PDF</div>
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>Review and adjust the fields below, then save</div>
              </div>
              <button type="button" onClick={() => { setPdfExtracted(false); pdfRef.current.click(); }}
                style={{ background: '#1e2329', border: '1px solid #2a3040', color: '#9ca3af', borderRadius: '6px', padding: '6px 12px', fontSize: '11px', cursor: 'pointer', flexShrink: 0 }}>
                Try another
              </button>
            </div>
          ) : (
            <div style={{ border: '2px dashed #2a3040', borderRadius: '8px', padding: '28px 16px', textAlign: 'center', cursor: pdfExtracting ? 'default' : 'pointer', opacity: pdfExtracting ? 0.7 : 1 }}
              onClick={() => !pdfExtracting && pdfRef.current.click()}>
              <i className="fa-solid fa-file-pdf" style={{ color: '#ef4444', fontSize: '26px', marginBottom: '8px', display: 'block' }} />
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                {pdfExtracting ? '🔍 Extracting data from PDF…' : 'Click to select a PDF — AI will read it and fill the form'}
              </div>
            </div>
          )}
          {pdfError && <div style={{ fontSize: '12px', color: '#fca5a5', marginTop: '8px', padding: '8px 10px', background: '#7f1d1d', borderRadius: '6px' }}>{pdfError}</div>}
        </div>
      )}

      {/* Paste URL */}
      {mode === 'url' && (
        <div style={{ borderTop: '1px solid #2a3040', paddingTop: '16px' }}>
          <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '8px' }}>
            Paste a link to the manufacturer's datasheet or product page — AI will extract data and the link is saved as reference
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input type="url" value={datasheetUrl} onChange={e => { onUrlChange(e.target.value); setUrlError(''); }}
              style={{ ...s.formInput, flex: 1 }} placeholder="https://…" autoFocus
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleUrlExtract(); } }} />
            {datasheetUrl && (
              <>
                <button type="button" onClick={handleUrlExtract} disabled={urlExtracting}
                  style={{ background: '#14b8a6', border: 'none', color: '#fff', borderRadius: '6px', padding: '8px 14px', fontSize: '12px', fontWeight: '600', cursor: urlExtracting ? 'default' : 'pointer', opacity: urlExtracting ? 0.7 : 1, flexShrink: 0 }}>
                  {urlExtracting ? '🔍 Reading…' : '🔍 Extract'}
                </button>
                <a href={datasheetUrl} target="_blank" rel="noreferrer"
                  style={{ background: '#0f1117', border: '1px solid #2a3040', color: '#9ca3af', borderRadius: '6px', padding: '8px 12px', fontSize: '12px', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                  <i className="fa-solid fa-arrow-up-right-from-square" style={{ fontSize: '10px' }} />
                </a>
                <button type="button" onClick={() => onUrlChange('')}
                  style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '11px', cursor: 'pointer', flexShrink: 0 }}>
                  Clear
                </button>
              </>
            )}
          </div>
          {urlError && <div style={{ fontSize: '12px', color: '#fca5a5', marginTop: '8px', padding: '8px 10px', background: '#7f1d1d', borderRadius: '6px' }}>{urlError}</div>}
        </div>
      )}
    </div>
  );
}

// ── MATERIAL FORM ─────────────────────────────────────────────
function MaterialForm({ material, defaultType, isDirector, onSaved, onCancel }) {
  const isNew = !material;
  const [form, setForm] = useState({
    type:           material?.type          || defaultType || 'material',
    name:           material?.name          || '',
    family:         material?.family        || '',
    manufacturer:   material?.manufacturer  || '',
    application:    material?.application   || '',
    colour:         material?.colour        || '',
    status:         material?.status        || 'active',
    tmelt_min:      material?.tmelt_min     || '',
    tmelt_max:      material?.tmelt_max     || '',
    tmould_rec:     material?.tmould_rec    || '',
    t_ejection:     material?.t_ejection    || '',
    tg:             material?.tg            || '',
    alpha:          material?.alpha         || '',
    cp:             material?.cp            || '',
    density:        material?.density       || '',
    dens_solid:     material?.dens_solid    || '',
    shear_max:      material?.shear_max     || '',
    mfi:            material?.mfi           || '',
    mfi_cond:       material?.mfi_cond      || '',
    shrink_l:       material?.shrink_l      || '',
    shrink_t:       material?.shrink_t      || '',
    back_pres_rec:  material?.back_pres_rec || '',
    screw_spd_rec:  material?.screw_spd_rec || '',
    cushion_rec:    material?.cushion_rec   || '',
    regrind_max:            material?.regrind_max            || '',
    residence_time_max_s:   material?.residence_time_max_s  || '',
    dry_required:   material?.dry_required  || false,
    dry_temp:       material?.dry_temp      || '',
    dry_time:       material?.dry_time      || '',
    dry_max:        material?.dry_max       || '',
    moisture_max_ppm: material?.moisture_max_ppm || '',
    dewpoint_target:  material?.dewpoint_target  || '',
    hygroscopic:    material?.hygroscopic   || false,
    cost_per_kg:    material?.cost_per_kg   || '',
    stock_kg:       material?.stock_kg      || '',
    stock_min_kg:   material?.stock_min_kg  || '',
    reorder_qty_kg: material?.reorder_qty_kg || '',
    supplier:       material?.supplier      || '',
    food_contact:   material?.food_contact  || false,
    medical_grade:  material?.medical_grade || false,
    rohs:           material?.rohs          || false,
    reach:          material?.reach         || false,
    notes:          material?.notes         || '',
    datasheet_url:  material?.datasheet_url || '',
  });
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [highlighted, setHighlighted] = useState(new Set());

  function set(key, value) { setForm(f => ({ ...f, [key]: value })); }

  function handleExtracted(data) {
    const NUMERIC = ['tmelt_min','tmelt_max','tmould_rec','t_ejection','tg','alpha','cp',
      'density','dens_solid','shear_max','mfi','shrink_l','shrink_t','regrind_max','residence_time_max_s',
      'dry_temp','dry_time','dry_max','moisture_max_ppm','dewpoint_target',
      'cost_per_kg','stock_kg','stock_min_kg','reorder_qty_kg'];
    const BOOL = ['dry_required','hygroscopic','food_contact','medical_grade','rohs','reach'];
    const filled = new Set();
    setForm(f => {
      const next = { ...f };
      Object.entries(data).forEach(([k, v]) => {
        if (v == null || !(k in next)) return;
        if (NUMERIC.includes(k)) next[k] = String(v);
        else if (BOOL.includes(k)) next[k] = Boolean(v);
        else next[k] = v;
        if (!BOOL.includes(k)) filled.add(k);
      });
      return next;
    });
    setHighlighted(filled);
    setTimeout(() => setHighlighted(new Set()), 3500);
  }

  const hl = k => highlighted.has(k);

  function save() {
    if (!form.name)   { setError('Name is required'); return; }
    if (!form.family) { setError('Family is required'); return; }
    setSaving(true);
    const payload = { ...form };
    ['tmelt_min','tmelt_max','tmould_rec','t_ejection','tg','alpha','cp','density',
     'dens_solid','shear_max','mfi','shrink_l','shrink_t','regrind_max','residence_time_max_s',
     'dry_temp','dry_time','dry_max','moisture_max_ppm','dewpoint_target',
     'cost_per_kg','stock_kg','stock_min_kg','reorder_qty_kg',
    ].forEach(k => { payload[k] = payload[k] !== '' ? parseFloat(payload[k]) : null; });

    const req = isNew
      ? api.post('/api/v1/materials', payload)
      : api.put(`/api/v1/materials/${material.id}`, payload);

    req.then(() => onSaved()).catch(err => {
      setError(err.response?.data?.error || 'Save failed');
      setSaving(false);
    });
  }

  const isMaterial = form.type === 'material';

  return (
    <div style={s.page}>
      <div style={s.detailHeader}>
        <button style={s.backBtn} onClick={onCancel}>← Cancel</button>
        <h1 style={s.title}>{isNew ? `Add ${form.type}` : `Edit — ${material.name}`}</h1>
      </div>
      {!isNew && <LastChanged type="material" id={material.id} style={{ margin: '-6px 0 14px' }} />}

      <DocumentPanel
        onExtracted={handleExtracted}
        datasheetUrl={form.datasheet_url}
        onUrlChange={url => set('datasheet_url', url)}
      />

      {error && <div style={s.formError}>{error}</div>}

      <div style={s.formGrid}>
        <FormSection title="Identity">
          <SelectField label="Type" value={form.type} onChange={v => set('type', v)}
            options={[{v:'material',l:'Material'},{v:'masterbatch',l:'Masterbatch'},{v:'additive',l:'Additive'}]}
            hl={hl('type')} />
          <Field label="Name *"         value={form.name}         onChange={v => set('name', v)}         hl={hl('name')} />
          <Field label="Family *"       value={form.family}       onChange={v => set('family', v)}       hl={hl('family')} placeholder="PC / ABS / PP / PA..." />
          <Field label="Manufacturer"   value={form.manufacturer} onChange={v => set('manufacturer', v)} hl={hl('manufacturer')} />
          <Field label="Application"    value={form.application}  onChange={v => set('application', v)}  hl={hl('application')} />
          <SelectField label="Status" value={form.status} onChange={v => set('status', v)}
            options={[{v:'active',l:'Active'},{v:'trial',l:'Trial'},{v:'discontinued',l:'Discontinued'}]} />
          {form.type === 'masterbatch' && (
            <div style={s.formField}>
              <label style={s.formLabel}>Colour</label>
              <ColourPicker value={form.colour || ''} onChange={v => set('colour', v)} />
            </div>
          )}
        </FormSection>

        {isMaterial && (
          <FormSection title="Processing temperatures">
            <Field label="Melt temp min (°C)"    value={form.tmelt_min}  onChange={v => set('tmelt_min', v)}  type="number" hl={hl('tmelt_min')} />
            <Field label="Melt temp max (°C)"    value={form.tmelt_max}  onChange={v => set('tmelt_max', v)}  type="number" hl={hl('tmelt_max')} />
            <Field label="Mould temp rec (°C)"   value={form.tmould_rec} onChange={v => set('tmould_rec', v)} type="number" hl={hl('tmould_rec')} />
            <Field label="Ejection temp (°C)"    value={form.t_ejection} onChange={v => set('t_ejection', v)} type="number" hl={hl('t_ejection')} />
            <Field label="Glass transition Tg (°C)" value={form.tg}     onChange={v => set('tg', v)}          type="number" hl={hl('tg')} />
          </FormSection>
        )}

        {isMaterial && (
          <FormSection title="Physics constants">
            <Field label="Thermal diffusivity α (m²/s)" value={form.alpha}      onChange={v => set('alpha', v)}      hl={hl('alpha')} />
            <Field label="Specific heat Cp (J/kg·K)"    value={form.cp}         onChange={v => set('cp', v)}         type="number" hl={hl('cp')} />
            <Field label="Melt density (kg/m³)"         value={form.density}    onChange={v => set('density', v)}    type="number" hl={hl('density')} />
            <Field label="Solid density (kg/m³)"        value={form.dens_solid} onChange={v => set('dens_solid', v)} type="number" hl={hl('dens_solid')} />
            <Field label="Max shear rate (s⁻¹)"         value={form.shear_max}  onChange={v => set('shear_max', v)}  type="number" hl={hl('shear_max')} />
            <Field label="MFI (g/10min)"                value={form.mfi}        onChange={v => set('mfi', v)}        type="number" hl={hl('mfi')} />
            <Field label="MFI conditions"               value={form.mfi_cond}   onChange={v => set('mfi_cond', v)}   hl={hl('mfi_cond')} />
            <Field label="Shrinkage L (%)"              value={form.shrink_l}   onChange={v => set('shrink_l', v)}   type="number" hl={hl('shrink_l')} />
            <Field label="Shrinkage T (%)"              value={form.shrink_t}   onChange={v => set('shrink_t', v)}   type="number" hl={hl('shrink_t')} />
          </FormSection>
        )}

        {isMaterial && (
          <FormSection title="Controller settings">
            <Field label="Back pressure rec (bar)" value={form.back_pres_rec} onChange={v => set('back_pres_rec', v)} placeholder="e.g. 50-150" hl={hl('back_pres_rec')} />
            <Field label="Screw speed rec (rpm)"   value={form.screw_spd_rec} onChange={v => set('screw_spd_rec', v)} placeholder="e.g. 30-80"  hl={hl('screw_spd_rec')} />
            <Field label="Cushion rec (mm)"        value={form.cushion_rec}   onChange={v => set('cushion_rec', v)} />
            <Field label="Max regrind (%)"              value={form.regrind_max}          onChange={v => set('regrind_max', v)}          type="number" />
            <Field label="Max residence time (s)"       value={form.residence_time_max_s} onChange={v => set('residence_time_max_s', v)} type="number" hint="Max time in barrel before thermal degradation. Defaults to 360s if not set." />
          </FormSection>
        )}

        {isMaterial && (
          <FormSection title="Drying">
            <CheckField label="Drying required" checked={form.dry_required} onChange={v => set('dry_required', v)} />
            {form.dry_required && <>
              <Field label="Drying temp (°C)"       value={form.dry_temp}         onChange={v => set('dry_temp', v)}         type="number" hl={hl('dry_temp')} />
              <Field label="Drying time min (h)"    value={form.dry_time}         onChange={v => set('dry_time', v)}         type="number" hl={hl('dry_time')} />
              <Field label="Drying time max (h)"    value={form.dry_max}          onChange={v => set('dry_max', v)}          type="number" hl={hl('dry_max')} />
              <Field label="Max moisture (ppm)"     value={form.moisture_max_ppm} onChange={v => set('moisture_max_ppm', v)} type="number" hl={hl('moisture_max_ppm')} />
              <Field label="Dewpoint target (°C)"   value={form.dewpoint_target}  onChange={v => set('dewpoint_target', v)}  type="number" hl={hl('dewpoint_target')} />
              <CheckField label="Hygroscopic"       checked={form.hygroscopic}    onChange={v => set('hygroscopic', v)} />
            </>}
          </FormSection>
        )}

        <FormSection title="Stock">
          <Field label="Current stock (kg)"  value={form.stock_kg}       onChange={v => set('stock_kg', v)}       type="number" />
          <Field label="Minimum stock (kg)"  value={form.stock_min_kg}   onChange={v => set('stock_min_kg', v)}   type="number" />
          <Field label="Reorder qty (kg)"    value={form.reorder_qty_kg} onChange={v => set('reorder_qty_kg', v)} type="number" />
          <Field label="Supplier"            value={form.supplier}       onChange={v => set('supplier', v)} />
        </FormSection>

        {/* Cost — director only */}
        {isDirector && (
          <FormSection title="Commercial">
            <Field label="Cost per kg (€)" value={form.cost_per_kg} onChange={v => set('cost_per_kg', v)} type="number" />
          </FormSection>
        )}

        <FormSection title="Compliance">
          <CheckField label="Food contact approved" checked={form.food_contact}  onChange={v => set('food_contact', v)} />
          <CheckField label="Medical grade"         checked={form.medical_grade} onChange={v => set('medical_grade', v)} />
          <CheckField label="RoHS compliant"        checked={form.rohs}          onChange={v => set('rohs', v)} />
          <CheckField label="REACH compliant"       checked={form.reach}         onChange={v => set('reach', v)} />
        </FormSection>

        <FormSection title="Notes">
          <Field label="Notes" value={form.notes} onChange={v => set('notes', v)} multiline />
        </FormSection>
      </div>

      <div style={s.formFooter}>
        <button style={s.backBtn} onClick={onCancel}>Cancel</button>
        <button style={{ ...s.saveBtn, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>
          {saving ? 'Saving...' : isNew ? 'Add' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

// ── SHARED COMPONENTS ─────────────────────────────────────────
function Spec({ label, value, highlight }) {
  return (
    <div style={s.spec}>
      <span style={s.specLabel}>{label}</span>
      <span style={{ ...s.specValue, color: highlight ? '#14b8a6' : '#f0f2f5' }}>{filled(value)}</span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={s.row}>
      <span style={s.rowLabel}>{label}</span>
      <span style={s.rowValue}>{filled(value)}</span>
    </div>
  );
}

function FormSection({ title, children }) {
  return (
    <div style={s.formSection}>
      <div style={s.formSectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', multiline, placeholder, hl }) {
  const inputStyle = { ...s.formInput, ...(hl ? s.hlInput : {}) };
  return (
    <div style={s.formField}>
      <label style={s.formLabel}>{label}</label>
      {multiline
        ? <textarea value={value} onChange={e => onChange(e.target.value)} style={s.formTextarea} rows={3} />
        : <input type={type} value={value} onChange={e => onChange(e.target.value)}
            style={inputStyle} placeholder={placeholder || ''} />
      }
    </div>
  );
}

function SelectField({ label, value, onChange, options, hl }) {
  const inputStyle = { ...s.formInput, ...(hl ? s.hlInput : {}) };
  return (
    <div style={s.formField}>
      <label style={s.formLabel}>{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} style={inputStyle}>
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

function CheckField({ label, checked, onChange }) {
  return (
    <div style={{ ...s.formField, flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        style={{ width: '16px', height: '16px' }} />
      <label style={{ ...s.formLabel, margin: 0 }}>{label}</label>
    </div>
  );
}

// ── STYLES ────────────────────────────────────────────────────
const s = {
  page:         cs.page,
  header:       cs.pageHeader,
  title:        cs.pageTitle,
  sub:          cs.pageSub,
  addBtn:       cs.btnPrimary,
  subTabs:      { display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '20px', borderBottom: `1px solid ${C.border}` },
  subTab:       { background: 'none', border: 'none', color: C.muted, fontSize: '13px', fontWeight: '600', padding: '8px 12px', cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: '-1px', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' },
  subTabActive: { color: C.text, borderBottom: `2px solid ${C.teal}` },
  tabCount:     { background: C.border, borderRadius: '10px', padding: '1px 6px', fontSize: '10px', fontWeight: '700' },
  grid:         { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' },
  card:         { background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'hidden', cursor: 'pointer' },
  cardBody:     { padding: '14px 18px' },
  cardHeader:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
  family:       { fontSize: '10px', fontWeight: '700', padding: '3px 8px', borderRadius: '8px' },
  stockDot:     { width: '8px', height: '8px', borderRadius: '50%' },
  matName:      { fontSize: '14px', fontWeight: '700', marginBottom: '2px' },
  manufacturer: { fontSize: '11px', color: C.muted, marginBottom: '12px' },
  specs:        { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' },
  spec:         { display: 'flex', flexDirection: 'column', gap: '2px' },
  specLabel:    { fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: C.muted },
  specValue:    { fontSize: '13px', fontWeight: '600', fontFamily: 'monospace' },
  cardFooter:   { padding: '10px 18px', borderTop: `1px solid ${C.border}` },
  statusChip:   { fontSize: '10px', fontWeight: '700', padding: '3px 8px', borderRadius: '10px' },
  empty:        { gridColumn: '1/-1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '200px', gap: '8px' },
  emptyText:    { fontSize: '16px', fontWeight: '700', color: C.muted },
  emptySub:     { fontSize: '13px', color: C.mutedDark },
  detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  detailActions:{ display: 'flex', gap: '8px' },
  titleRow:     { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' },
  typeChip:     { fontSize: '10px', fontWeight: '700', padding: '3px 8px', borderRadius: '8px', background: C.border, color: '#9ca3af', textTransform: 'capitalize' },
  detailTitle:  { fontSize: '24px', fontWeight: '700', marginBottom: '4px' },
  detailSub:    { fontSize: '13px', color: C.muted, marginBottom: '20px' },
  notesBox:     { background: '#1e3a5f', border: '1px solid #1d4ed8', borderRadius: '10px', padding: '14px 16px' },
  notesTitle:   { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#93c5fd', marginBottom: '6px' },
  notesText:    { fontSize: '13px', color: '#bfdbfe', lineHeight: '1.6' },
  detailGrid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' },
  section:      cs.card,
  sectionTitle: { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: C.teal, marginBottom: '12px' },
  row:          { display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${C.border}` },
  rowLabel:     { fontSize: '12px', color: C.muted },
  rowValue:     { fontSize: '12px', fontWeight: '600', fontFamily: 'monospace' },
  backBtn:      cs.btnSecondary,
  editBtn:      { background: '#1d4ed8', color: '#bfdbfe', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  deleteBtn:    { background: C.dangerBg, color: C.dangerText, border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  formGrid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px', marginBottom: '24px' },
  formSection:  cs.card,
  formSectionTitle: { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: C.teal, marginBottom: '12px' },
  formField:    { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px' },
  formLabel:    cs.label,
  formInput:    cs.input,
  formTextarea: { ...cs.input, resize: 'vertical' },
  formFooter:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '8px' },
  formError:    cs.errorBanner,
  saveBtn:      { ...cs.btnPrimary, padding: '10px 24px' },
  center:       { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'system-ui, sans-serif', color: C.muted },
  extractor:    { ...cs.card, borderRadius: '12px', marginBottom: '20px' },
  dropZone:     { border: `2px dashed ${C.border}`, borderRadius: '8px', padding: '20px 16px', textAlign: 'center', cursor: 'pointer' },
  previewImg:   { width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', border: `1px solid ${C.border}`, display: 'block' },
  removePreview:{ position: 'absolute', top: '-6px', right: '-6px', background: C.danger, border: 'none', color: '#fff', borderRadius: '50%', width: '18px', height: '18px', cursor: 'pointer', fontSize: '13px', lineHeight: '18px', textAlign: 'center', padding: 0 },
  scanBtn:      { width: '100%', background: '#1d4ed8', color: '#bfdbfe', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  hlInput:      { borderColor: C.teal, boxShadow: '0 0 0 2px rgba(20,184,166,0.25)', transition: 'box-shadow 0.3s, border-color 0.3s' },
  overlay:      cs.overlay,
  modal:        cs.modal,
  modalHeader:  cs.modalHeader,
  modalTitle:   cs.modalTitle,
  modalBody:    cs.modalBody,
  modalFooter:  cs.modalFooter,
  closeBtn:     cs.closeBtn,
  viewToggleBtn:    { background: 'none', border: `1px solid ${C.border}`, color: C.muted, borderRadius: '6px', padding: '5px 10px', fontSize: '11px', cursor: 'pointer', fontFamily: 'system-ui, sans-serif' },
  viewToggleBtnActive: { background: C.border, color: C.text, borderColor: '#3a4050' },
};